import type { MapMouseEvent } from 'react-map-gl/mapbox';
import { EditorMode } from '~/common/types';
import layerStore from '~/store2/layers';
import type { MapStoreBase } from '.';
import { isMapClickOwnedByTab } from './mapClickOwnership';
import { deriveFeatureIdentity } from './Widgets/MetadataSidebar/buildSidebarSections';
import {
  calcBoundingBox,
  getIdentifiableLayerIds,
  hitsProjectBoundary,
  NO_FEATURES_FOUND_MESSAGE,
} from './Widgets/MetadataSidebar/metadataSidebar.helpers';

/**
 * Left-click identify: with no draw/select tool active, hit-test the same layers the sidebar queries.
 * A hit opens the panel (same call as the context menu's "Layer Attributes"); an empty click closes it
 * if open and announces that nothing was found.
 */
export function identifyOnMapClick(e: MapMouseEvent, store: MapStoreBase) {
  if (store.editorMode !== EditorMode.None) return;

  // A select-on-map tab (Land Rights / Zoning / Intelligence) owns left-click for its
  // own selection; defer and close any stale panel.
  if (isMapClickOwnedByTab()) {
    if (store.identifyDetailsPoint) store.setIdentifyDetailsPoint(undefined);
    return;
  }

  const map = e.target;
  // Project boundary wins: a click on a project boundary (own-org `project-polygon` or a delegated
  // `project-polygon-${orgId}`) defers to its own click handler (opens the project card), so skip
  // identify and close any open panel. No-op on maps without a project-boundary layer.
  const boundaryHit = hitsProjectBoundary(
    map.queryRenderedFeatures(calcBoundingBox(e.point, 10)).map((f) => f.layer?.id),
  );
  if (boundaryHit) {
    if (store.identifyDetailsPoint) store.setIdentifyDetailsPoint(undefined);
    return;
  }
  const queryLayerIds = getIdentifiableLayerIds(map, layerStore);
  // Never bare-query (empty layers would match the basemap). With nothing identify-able,
  // close an open panel and announce that nothing was found.
  if (queryLayerIds.length === 0) {
    if (store.identifyDetailsPoint) store.setIdentifyDetailsPoint(undefined);
    store.announceSelection(NO_FEATURES_FOUND_MESSAGE);
    return;
  }

  // Hit-test the same ~10px box the sidebar's content query uses, so thin lines/points are
  // clickable and a click identifies exactly what the panel will then show.
  const hits = map.queryRenderedFeatures(calcBoundingBox(e.point, 10), { layers: queryLayerIds });
  if (hits.length > 0) {
    // Single-select the ONE topmost feature. Custom-data hits are a whole layer,
    // not an individual feature; they (and any hit with no identity) open in area mode.
    // Identity derivation is wrapped so a malformed feature falls back to area, not a broken click.
    let selectedFeatureKey: string | undefined;
    try {
      const identity = hits[0] ? deriveFeatureIdentity(hits[0], layerStore.tilesConfig) : null;
      selectedFeatureKey = identity && !identity.isCustomData ? identity.sectionKey : undefined;
    } catch {
      selectedFeatureKey = undefined;
    }
    store.setIdentifyDetailsPoint({ lng: e.lngLat.lng, lat: e.lngLat.lat }, selectedFeatureKey);
  } else {
    if (store.identifyDetailsPoint) store.setIdentifyDetailsPoint(undefined);
    store.announceSelection(NO_FEATURES_FOUND_MESSAGE);
  }
}
